/* eslint-disable prefer-arrow-callback, react/button-has-type */
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { addUtils, applyDefaultClass } from './class-name-helpers';
import CheckIcon from '../components/icons/Check';

const basePropTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  primary: PropTypes.bool,
  secondary: PropTypes.bool,
  round: PropTypes.bool,
  frameless: PropTypes.bool,
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  dossierPrimary: PropTypes.bool,
  dossierSecondary: PropTypes.bool,
};

const baseDefaultProps = {
  className: undefined,
  primary: false,
  secondary: false,
  round: false,
  frameless: false,
  active: false,
  disabled: false,
  dossierPrimary: false,
  dossierSecondary: false,
};

export const Button = React.forwardRef(
  function Button({
    children,
    type,
    className,
    primary,
    secondary,
    round,
    frameless,
    active,
    dossierPrimary,
    dossierSecondary,
    disabled,
    ...props
  }, ref) {
    const variants = {
      'ss_btn--primary': primary,
      'ss_btn--secondary': secondary,
      'ss_btn--round': round,
      'ss_btn--frameless': frameless,
      'ss_btn--dossier-primary': dossierPrimary,
      'ss_btn--dossier-secondary': dossierSecondary,
    };
    const defaultClassName = applyDefaultClass(variants, 'ss_btn--primary');
    const { restProps, utilClassNames } = addUtils(props);

    return (
      <button
        className={clsx('ss_btn', className, utilClassNames, variants, defaultClassName, active && 'ss_active')}
        type={type}
        ref={ref}
        disabled={disabled}
        {...restProps}
      >
        {children}
      </button>
    );
  },
);

Button.propTypes = {
  ...basePropTypes,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
};

Button.defaultProps = {
  ...baseDefaultProps,
  type: 'button',
};

export const LinkButton = React.forwardRef(
  function LinkButton({
    children,
    className,
    primary,
    secondary,
    round,
    frameless,
    active,
    disabled,
    dossierPrimary,
    dossierSecondary,
    ...props
  }, ref) {
    const variants = {
      'ss_btn--primary': primary,
      'ss_btn--secondary': secondary,
      'ss_btn--round': round,
      'ss_btn--frameless': frameless,
      'ss_btn--dossier-primary': dossierPrimary,
      'ss_btn--dossier-secondary': dossierSecondary,
    };
    const defaultClassName = applyDefaultClass(variants, 'ss_btn--primary');
    const { restProps, utilClassNames } = addUtils(props);

    return (
      <a
        className={clsx(
          'ss_btn',
          className,
          utilClassNames,
          variants,
          defaultClassName,
          active && 'ss_active',
          disabled && 'ss_btn--disabled',
        )}
        ref={ref}
        {...restProps}
      >
        {children}
      </a>
    );
  },
);

LinkButton.propTypes = basePropTypes;
LinkButton.defaultProps = baseDefaultProps;

export const ToggleButton = React.forwardRef(
  function ToggleButton({
    children,
    className,
    primary,
    secondary,
    round,
    frameless,
    active,
    dossierPrimary,
    onClick,
    checked,
    ...props
  }, ref) {
    const variants = {
      'ss_btn--primary': primary,
      'ss_btn--secondary': secondary,
      'ss_btn--round': round,
      'ss_btn--frameless': frameless,
      'ss_btn--dossier-primary': dossierPrimary,
    };
    const defaultClassName = applyDefaultClass(variants, 'ss_btn--primary');
    const { restProps, utilClassNames } = addUtils(props);

    const checkedClass = checked && 'ss_btn--dossier-primary-checked';

    const handleClick = (e) => {
      onClick(e);
    };

    return (
      <button
        className={clsx(
          'ss_btn',
          className,
          utilClassNames,
          variants,
          defaultClassName,
          active && 'ss_active',
          checkedClass,
        )}
        ref={ref}
        onClick={handleClick}
        {...restProps}
      >
        {children}
        {' '}
        {checked && (<CheckIcon fill="black" style={{ transform: 'translate(6px, 3px)' }} />) }
      </button>
    );
  },
);

ToggleButton.propTypes = {
  ...basePropTypes,
  onClick: PropTypes.func,
  checked: PropTypes.bool,
};

ToggleButton.defaultProps = {
  ...baseDefaultProps,
  onClick: () => undefined,
  checked: false,
};
